const VIRTUAL_CAMERA_KEYWORDS = ['virtual', 'obs', 'xsplit', 'manycam', 'cam studio', 'droid cam', 'ip cam', 'vmix', 'screen', 'capture', 'fake', 'mock', 'droidcam'];

export function analyzeVirtualCamera(videoTrack, settings) {
    const label = videoTrack.label || '';
    const factors = [];
    let isVirtual = false;

    // 1. Comprobar palabras clave en nombre
    const lowerLabel = label.toLowerCase();
    const matchingKeywords = VIRTUAL_CAMERA_KEYWORDS.filter(keyword =>
      lowerLabel.includes(keyword)
    );

    if (matchingKeywords.length > 0) {
      factors.push(`Nombre sospechoso (${matchingKeywords.join(', ')})`);
      isVirtual = true;
    }

    // 2. Comprobar resoluciones perfectamente estándar
    const commonResolutions = [
      {width: 640, height: 480},
      {width: 1280, height: 720},
      {width: 1920, height: 1080}
    ];

    const hasExactResolution = commonResolutions.some(res =>
      res.width === settings.width && res.height === settings.height
    );

    if (hasExactResolution && matchingKeywords.length > 0) {
      factors.push("Resolución exactamente estándar");
    }

    // 3. Comprobar framerate exacto
    if (settings.frameRate && Number.isInteger(settings.frameRate) && matchingKeywords.length > 0) {
      factors.push("Framerate exacto (sin decimales)");
    }

    return isVirtual;
  }

export async function analyzeVirtualCameraV2(videoDevices) {
    const results = [];

    for (const device of videoDevices) {
        const isVirtual = await isVirtualCamera(device.deviceId);
        results.push({ deviceId: device.deviceId, label: device.label, isVirtual });
    }

    return results;
}

function isIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
async function isVirtualCamera(deviceId) {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: deviceId } }
        });

        const [track] = stream.getVideoTracks();
        const settings = track.getSettings?.();
        const capabilities = track.getCapabilities?.();

        let score = 0;

        if (!isIOS()) {
            // ---- Heurísticas para desktop (Mac/Windows) ----
            if (!settings?.facingMode) score++;
            if (
                capabilities?.width &&
                capabilities?.height &&
                capabilities.width.max === capabilities.width.min &&
                capabilities.height.max === capabilities.height.min
            ) {
                score++;
            }
            if (
                capabilities?.frameRate &&
                capabilities.frameRate.max === capabilities.frameRate.min
            ) {
                score++;
            }
        } else {
            // ---- Heurísticas para iOS ----
            // En iOS, confiar en groupId y facingMode
            if (!settings?.facingMode) score++;
            // groupId inexistente → sospechoso
            if (!track.getSettings?.().groupId) score++;
        }

        // ---- Frame analysis como refuerzo ----
        const video = document.createElement("video");
        video.srcObject = stream;
        video.muted = true;
        await video.play();

        await new Promise((res) => setTimeout(res, 600));
        const canvas = document.createElement("canvas");
        canvas.width = settings?.width || 320;
        canvas.height = settings?.height || 240;
        const ctx = canvas.getContext("2d");

        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame1 = ctx?.getImageData(0, 0, canvas.width, canvas.height);

        await new Promise((res) => setTimeout(res, 500));
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame2 = ctx?.getImageData(0, 0, canvas.width, canvas.height);

        if (frame1 && frame2) {
            let diff = 0;
            for (let i = 0; i < frame1.data.length; i += 40) {
                if (
                    Math.abs(frame1.data[i] - frame2.data[i]) > 15 ||
                    Math.abs(frame1.data[i + 1] - frame2.data[i + 1]) > 15 ||
                    Math.abs(frame1.data[i + 2] - frame2.data[i + 2]) > 15
                ) {
                    diff++;
                }
            }
            const changeRatio = diff / (frame1.data.length / 40);
            if (changeRatio < 0.001) score++;
        }

        // Cleanup
        track.stop();
        stream.getTracks().forEach((t) => t.stop());

        // ---- Decisión distinta según plataforma ----
        if (isIOS()) {
            return score >= 2; // en iOS, reglas más relajadas
        }
        return score >= 2; // en desktop, mantenerlo estricto
    } catch (err) {
        console.error("Error checking camera", err);
        return false;
    }
}

export async function validateCamera(stream, isMac) {
    try {
        const videoTrack = stream.getVideoTracks()[0];
        const settings = videoTrack.getSettings?.();
        const capabilities = videoTrack.getCapabilities?.();

        let score = 0;

        // Detectar iOS
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (!isIOS) {
            // ---- Heurísticas para Mac/Windows ----
            if (!settings?.facingMode) score++;
            if (
                capabilities?.width &&
                capabilities?.height &&
                capabilities.width.max === capabilities.width.min &&
                capabilities.height.max === capabilities.height.min
            ) {
                score++;
            }
            if (
                capabilities?.frameRate &&
                capabilities.frameRate.max === capabilities.frameRate.min
            ) {
                score++;
            }
        } else {
            // ---- Heurísticas para iOS ----
            if (!settings?.facingMode) score++;
            if (!settings?.groupId) score++;
        }

        // ---- Análisis de frame como refuerzo ----
        const video = document.createElement("video");
        video.srcObject = stream;
        video.muted = true;
        await video.play();

        await new Promise((res) => setTimeout(res, 600));
        const canvas = document.createElement("canvas");
        canvas.width = settings?.width || 320;
        canvas.height = settings?.height || 240;
        const ctx = canvas.getContext("2d");

        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame1 = ctx?.getImageData(0, 0, canvas.width, canvas.height);

        await new Promise((res) => setTimeout(res, 500));
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame2 = ctx?.getImageData(0, 0, canvas.width, canvas.height);

        if (frame1 && frame2) {
            let diff = 0;
            for (let i = 0; i < frame1.data.length; i += 40) {
                if (
                    Math.abs(frame1.data[i] - frame2.data[i]) > 15 ||
                    Math.abs(frame1.data[i + 1] - frame2.data[i + 1]) > 15 ||
                    Math.abs(frame1.data[i + 2] - frame2.data[i + 2]) > 15
                ) {
                    diff++;
                }
            }
            const changeRatio = diff / (frame1.data.length / 40);
            if (changeRatio < 0.001) score++;
        }

        // ---- Decisión ----
        const isVirtual = isIOS ? score >= 2 : score >= 2;

        // Si es virtual → cerramos el stream
        if (isVirtual) {
            stream.getTracks().forEach((t) => t.stop());
        }

        return isVirtual;
    } catch (err) {
        console.error("Error validating camera", err);
        return false;
    }
}
